import styled from "@emotion/styled";
import { useCallback, useMemo } from "react";

import {
  FONT_WEIGHT_NORMAL,
  FONT_WEIGHT_SEMI_BOLD,
  FieldSearch,
  FormRowField,
  FormikForm,
  IconButton,
  SkiliticsIcon,
  type FormFormatMessages,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import type { NodeTemplatesSearchCriteria } from "../types";
import { FormBody, StyledForm } from "./shared";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  flex: 0 0 65px;
  width: 100%;

  color: ${({ theme }) => theme.generalTextIcon};

  font-size: 18px;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  line-height: 24px;
  padding-inline: 20px;

  background-color: ${({ theme }) => theme.surfaceSoft};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;

  & span {
    display: flex;

    user-select: none;
  }

  & span:last-of-type {
    font-weight: ${FONT_WEIGHT_NORMAL};
    margin-inline-start: -5px;
  }
`;

const Text = styled.span`
  white-space: nowrap;
`;

type FormValues = Pick<Required<NodeTemplatesSearchCriteria>, "search">;

type Props = {
  search: NodeTemplatesSearchCriteria["search"];
  onChange: (newCriteria: NodeTemplatesSearchCriteria) => void;
  onClose: () => void;
  libraryLabel: string;
};

export const Heading = ({ search, onChange, onClose, libraryLabel }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const initialValues = useMemo((): FormValues => ({ search: search ?? "" }), [search]);

  const handleSubmit = useCallback(
    (values: FormValues) => onChange({ search: values.search || undefined }),
    [onChange],
  );

  return (
    <Container data-test="heading-container">
      <Title data-test="title-container">
        <SkiliticsIcon icon="library" size={22} />
        <Text>{libraryLabel}</Text>
        <Text>{formatMessage("library")}</Text>
      </Title>

      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnBlur
        formatStrings={formFormatStrings}
        dataTest="search-form"
      >
        <StyledForm>
          <FormBody>
            <FormRowField<FormValues> name="search">
              <FieldSearch borderRadius="50px" fullWidth />
            </FormRowField>
          </FormBody>
        </StyledForm>
      </FormikForm>
      <IconButton icon="xmark" onClick={onClose} data-test="close-button" />
    </Container>
  );
};

const formatStrings = {
  library: {
    en: "Library",
  },
} satisfies FormatMessages;

const formFormatStrings = {
  searchLabel: {
    en: "",
  },
  searchPlaceholder: {
    en: "Search",
  },
} satisfies FormFormatMessages<FormValues>;
